//diary-page.tsx

import { useEffect, useState, useContext, useRef } from "react";
import { Button, Box } from "@mui/material";
import { useHistory, Link } from "react-router-dom";
import Page from "../components/page";
import { ErrorContext, UserContext } from "../components/app-routes";
import { StripedDataGrid } from "../components/striped-data-grid";
import {
  GridColDef,
  GridRowId,
  GridRowClassNameParams,
  GridRowSelectionModel,
  GridRowParams,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import { DiaryEntry } from "../models/diary-entry";
import DeleteIcon from "@mui/icons-material/Delete";
import { diaryApi } from "../services/api/diary";
import IconButton from "@mui/material/IconButton";
import ConfirmDialog from "../components/confirm-dialog";
import { ActiveFilterControls } from "../components/active-filter-controls";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LoadingBackdrop from "../components/loading-backdrop";
import { DateTime } from "luxon";
import { getSearchParamNumber } from "../utils/history";
import mixpanel from "mixpanel-browser";
import { getCustomLabel } from "../utils/custom-labels";

const showArchivedCheckedStorageKey = "diaryShowArchivedChecked";
const showActiveCheckedStorageKey = "diaryShowActiveChecked";

export default function DiaryPage() {
  const history = useHistory();
  const { handleError } = useContext(ErrorContext);
  const { grower } = useContext(UserContext);
  if (!grower) throw new Error("Invalid context");
  const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );
  const page = getSearchParamNumber(history, "page", 0);
  const nowInGrowerZone = DateTime.now().setZone(grower.TimeZone || "UTC");
  const dateLabel = nowInGrowerZone.toFormat("MMM d, yyyy");
  const csvFileName = `SeedLeaf Diary - ${dateLabel}`;
  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  useEffect(() => {
    isMounted.current = true;

    mixpanel.track("Visit Diary Page");

    async function loadDiary() {
      setLoading(true);
      try {
        const data = await diaryApi.getAll();
        if (isMounted.current) {
          setDiaryEntries(data);
        }
      } catch (err) {
        if (isMounted.current) {
          handleError(err);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    }

    loadDiary();

    return () => {
      isMounted.current = false;
    };
  }, [handleError]);

  const [selected, setSelected] = useState<number[]>([]);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleDeleteDialogClose = async (okDelete?: boolean) => {
    setDialogOpen(false);
    if (!okDelete || !selected) return;

    setBusy(true);

    try {
      if (Array.isArray(selected)) {
        for (const id of selected) {
          await diaryApi.delete(id);
        }
      }

      const data = await diaryApi.getAll();
      if (isMounted.current) {
        setDiaryEntries(data);
        setSelected([]);
      }
    } catch (err) {
      if (isMounted.current) {
        handleError(err);
      }
    } finally {
      if (isMounted.current) {
        setBusy(false);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      hideable: false,
    },
    {
      field: "archived",
      headerName: "",
      width: 40,
      minWidth: 40,
      sortable: false,
      filterable: false,
      align: "center",
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) =>
        params.row.archivedAt ? (
          <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
        ) : null,
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 150,
      renderCell: (params) =>
        new Date(
          params.value as unknown as string | number | Date,
        ).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
    },
    {
      field: "subject",
      headerName: "Subject",
      width: 250,
      renderCell: (params) => <span className="cell-text">{params.value}</span>,
    },
    {
      field: "enteredBy",
      headerName: "Entered By",
      width: 180,
      renderCell: (params) => <span className="cell-text">{params.value}</span>,
    },
    {
      field: "tags",
      headerName: "Tags",
      width: 180,
      renderCell: (params) => <span className="cell-text">{params.value}</span>,
    },
    {
      field: "body",
      headerName: "Body",
      width: 300,
      renderCell: (params) => <span className="cell-text">{params.value}</span>,
    },
  ];

  const handleRowClick = (id: GridRowId) => {
    history.push(`/home/diary/${id}`);
  };
  const selectedCount = selected.length;

  return (
    <Page title={getCustomLabel(grower ?? null, "diary")}>
      <Button
        variant="contained"
        color="primary"
        component={Link}
        to="/home/diary/add"
        onClick={() => mixpanel.track("Click Add Diary Entry")}
      >
        Add
      </Button>
      <IconButton
        aria-label="delete"
        disabled={busy || selectedCount === 0}
        onClick={() => setDialogOpen(true)}
      >
        <DeleteIcon />
      </IconButton>
      <ActiveFilterControls
        showActiveChecked={showActiveChecked}
        showArchivedChecked={showArchivedChecked}
        onChangeShowActive={handleShowActiveChanged}
        onChangeShowArchived={handleShowArchivedChanged}
      />
      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <StripedDataGrid
          storageKey="diary-grid"
          getRowClassName={(params: GridRowClassNameParams) => {
            const row = params.row as any;

            if (row.archivedAt) {
              return "archived-row";
            }

            return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
          }}
          columns={columns}
          rows={
            diaryEntries?.filter(
              (entry) =>
                (showActiveChecked && entry.archivedAt === null) ||
                (showArchivedChecked && entry.archivedAt !== null),
            ) ?? []
          }
          initialState={{
            columns: {
              columnVisibilityModel: {
                body: false,
              },
            },
          }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowClick={(params: GridRowParams, event: React.MouseEvent) => {
            const target = event.target as HTMLElement;
            if (
              target.closest(".MuiDataGrid-cellCheckbox") ||
              target.closest('input[type="checkbox"]')
            ) {
              return;
            }

            handleRowClick(params.id);
          }}
          onRowSelectionModelChange={handleSelectionChange}
          slots={{
            toolbar: GridToolbar,
          }}
          showToolbar
          pagination
          paginationMode="client"
          pageSizeOptions={[15, 25, 50, 100]}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: {
                allColumns: true,
                fileName: csvFileName,
              },
            },
          }}
        />
        {selected && (
          <ConfirmDialog
            title="Delete Notes"
            description={`Are you sure you want to delete ${selectedCount} note${
              selectedCount !== 1 ? "s" : ""
            }?`}
            open={dialogOpen}
            onClose={handleDeleteDialogClose}
          />
        )}
        <LoadingBackdrop open={loading || busy} />
      </Box>
    </Page>
  );
}
